/* global axios */
import ApiClient from './ApiClient';

class CalendarAPI extends ApiClient {
  constructor() {
    super('calendars', { accountScoped: true });
  }

  // Calendars
  getCalendars() {
    return axios.get(this.url);
  }

  getCalendar(calendarId) {
    return axios.get(`${this.url}/${calendarId}`);
  }

  createCalendar(data) {
    return axios.post(this.url, data);
  }

  updateCalendar(calendarId, data) {
    return axios.patch(`${this.url}/${calendarId}`, data);
  }

  deleteCalendar(calendarId) {
    return axios.delete(`${this.url}/${calendarId}`);
  }

  getAvailability(calendarId, { date, agentId, appointmentTypeId }) {
    return axios.get(`${this.url}/${calendarId}/availability`, {
      params: {
        date,
        agent_id: agentId,
        appointment_type_id: appointmentTypeId,
      },
    });
  }

  // Appointment Types
  getAppointmentTypes(calendarId) {
    return axios.get(`${this.url}/${calendarId}/appointment_types`);
  }

  createAppointmentType(calendarId, data) {
    return axios.post(`${this.url}/${calendarId}/appointment_types`, data);
  }

  updateAppointmentType(calendarId, typeId, data) {
    return axios.patch(
      `${this.url}/${calendarId}/appointment_types/${typeId}`,
      data
    );
  }

  deleteAppointmentType(calendarId, typeId) {
    return axios.delete(`${this.url}/${calendarId}/appointment_types/${typeId}`);
  }

  // Appointment Reminders
  getReminders(calendarId) {
    return axios.get(`${this.url}/${calendarId}/appointment_reminders`);
  }

  createReminder(calendarId, data) {
    // When data is FormData (file upload), axios auto-sets Content-Type with boundary.
    // Do NOT set Content-Type manually — missing boundary causes the server to get 0-byte files.
    return axios.post(`${this.url}/${calendarId}/appointment_reminders`, data);
  }

  updateReminder(calendarId, reminderId, data) {
    return axios.patch(
      `${this.url}/${calendarId}/appointment_reminders/${reminderId}`,
      data
    );
  }

  deleteReminder(calendarId, reminderId) {
    return axios.delete(
      `${this.url}/${calendarId}/appointment_reminders/${reminderId}`
    );
  }

  // ElevenLabs Integration
  getElevenLabsVoices() {
    return axios.get(`${this.baseUrl()}/integrations/eleven_labs/voices`);
  }

  previewElevenLabsAudio(text, voiceId) {
    return axios.post(
      `${this.baseUrl()}/integrations/eleven_labs/preview`,
      { text, voice_id: voiceId },
      { responseType: 'blob' }
    );
  }

  // Appointments
  getAppointments(params = {}) {
    const queryParams = { ...params };
    const searchParams = new URLSearchParams();

    Object.keys(queryParams).forEach(key => {
      const value = queryParams[key];
      if (Array.isArray(value)) {
        value.forEach(v => {
          searchParams.append(`${key}[]`, v);
        });
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, value);
      }
    });

    const queryString = searchParams.toString();
    return axios.get(`${this.baseUrl()}/appointments?${queryString}`);
  }

  getAppointment(appointmentId) {
    return axios.get(`${this.baseUrl()}/appointments/${appointmentId}`);
  }

  createAppointment(data) {
    return axios.post(`${this.baseUrl()}/appointments`, data);
  }

  updateAppointment(appointmentId, data) {
    return axios.patch(`${this.baseUrl()}/appointments/${appointmentId}`, data);
  }

  deleteAppointment(appointmentId) {
    return axios.delete(`${this.baseUrl()}/appointments/${appointmentId}`);
  }

  confirmAppointment(appointmentId) {
    return axios.post(`${this.baseUrl()}/appointments/${appointmentId}/confirm`);
  }

  cancelAppointment(appointmentId, reason) {
    return axios.post(`${this.baseUrl()}/appointments/${appointmentId}/cancel`, {
      reason,
    });
  }

  completeAppointment(appointmentId) {
    return axios.post(
      `${this.baseUrl()}/appointments/${appointmentId}/complete`
    );
  }

  markNoShow(appointmentId) {
    return axios.post(`${this.baseUrl()}/appointments/${appointmentId}/no_show`);
  }

  getAppointmentsByContact(contactId) {
    return axios.get(`${this.baseUrl()}/appointments/by_contact`, {
      params: { contact_id: contactId },
    });
  }

  getAppointmentsByConversation(conversationId) {
    return axios.get(`${this.baseUrl()}/appointments/by_conversation`, {
      params: { conversation_id: conversationId },
    });
  }

  getUpcomingAppointments(limit = 50) {
    return axios.get(`${this.baseUrl()}/appointments/upcoming`, {
      params: { limit },
    });
  }

  getTodayAppointments() {
    return axios.get(`${this.baseUrl()}/appointments/today`);
  }
}

export default new CalendarAPI();
